/* eslint-disable no-console */
import { Col, Row } from "antd";
import React, { useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { CompletedTripData } from "../Data/TripDetailData";
import CustomRoundedImage from "../../../Core/Components/CustomRoundedImage";
import StopDetails from "../TripDetails/StopDetails";
// import CompletedTripInformation from "../TripDetails/CompletedTripInformation";
import { updateTrips } from "../../../Redux/Slices/trips.slice";
import UpdateIcon from "../../../Common/Svgs/UpdateIcon";
import SpoiltMilkTooltip from "./SpoiltMilkTooltip";
// import TripInformation from "../TripDetails/TripInformation";
import TripRequests from "./TripRequests";
import { InfoCircleSecondaryIcon } from "../../../Common/Svgs/InfoCircleIcon";

const KpiAlertTooltip = ({ text }) => {
  const [hover, setHover] = useState(false);

  return (
    <div className="relative inline-flex items-center">
      <div
        className="cursor-pointer flex items-center"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <InfoCircleSecondaryIcon
          bgColor="#C84630"
          fillColor="#ffffff"
          className="flex items-center"
        />
      </div>

      {hover && (
        <div className="absolute z-[999999] bottom-full left-1/2 -translate-x-1/2 mb-2 bg-dark-gray px-2.5 py-1.5 rounded-md whitespace-nowrap shadow-lg pointer-events-none">
          <p className="font-aileron text-10 font-normal text-white text-center">
            {text}
          </p>
          <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-dark-gray" />
        </div>
      )}
    </div>
  );
};

const TripSummaryStops = () => {
  const dispatch = useDispatch();

  const { tripDetailsKPI, overallSummaryForTrip } = useSelector(
    (state) => state.trips,
  );

  const startTime = tripDetailsKPI?.startTime;
  const endTime = tripDetailsKPI?.endTime;
  let differenceInMinutes = 0;

  if (startTime && endTime) {
    differenceInMinutes = dayjs(endTime).diff(dayjs(startTime), "minutes");
  }

  const isDurationExceeded = differenceInMinutes > 120;
  const excessMinutes = isDurationExceeded ? differenceInMinutes - 120 : 0;
  const excessHours = Math.floor(excessMinutes / 60);
  const excessMins = excessMinutes % 60;
  const durationExceededTooltip = `trip duration exceeded by ${
    excessHours > 0 ? `${excessHours}h ` : ""
  }${excessMins}m`;

  const totalCollected = parseFloat(tripDetailsKPI?.totalCollected) || 0;
  const totalDelivered = parseFloat(tripDetailsKPI?.totalDelivered) || 0;
  let variancePct = 0;
  if (totalCollected > 0) {
    variancePct =
      (Math.abs(totalCollected - totalDelivered) / totalCollected) * 100;
  }
  const isVarianceDetected = variancePct > 5;
  const varianceTooltip = `${variancePct
    .toFixed(1)
    .replace(/\.0$/, "")}% of variance detected`;

  return (
    <div className="flex flex-col p-6 bg-background h-[calc(100vh-128px)] overflow-y-scroll">
      <Row gutter={8}>
        {CompletedTripData(tripDetailsKPI)
          ?.slice(0, 3)
          ?.map((kpi) => {
            let hours = 0;
            let minutes = 0;

            if (startTime && endTime) {
              hours = Math.floor(differenceInMinutes / 60); // Get the hours
              minutes = differenceInMinutes % 60; // Get the remaining minutes
            }

            return (
              <Col span={8} key={kpi.id}>
                <div className="p-4 rounded-md bg-kpi-background">
                  <div className="flex items-center gap-x-2.5">
                    <CustomRoundedImage
                      background="bg-kpi-icon"
                      image={kpi.icon}
                    />
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs font-normal capitalize font-aileron text-trip-kpi-title">
                        {kpi.title}
                      </p>
                      {kpi.id === 3 && isDurationExceeded && (
                        <KpiAlertTooltip text={durationExceededTooltip} />
                      )}
                    </div>
                  </div>

                  {kpi.id === 3 ? (
                    <div className="ml-12 flex items-center gap-x-1">
                      {hours >= 0 && (
                        <p
                          className={`text-2xl font-semibold font-poppins ${
                            isDurationExceeded
                              ? "text-[#C84630]"
                              : "text-trip-kpi-value"
                          }`}
                        >
                          {hours}
                          <span className="text-base ml-0.5">H</span>
                        </p>
                      )}

                      {minutes >= 0 && (
                        <p
                          className={`text-2xl font-semibold font-poppins ${
                            isDurationExceeded
                              ? "text-[#C84630]"
                              : "text-trip-kpi-value"
                          }`}
                        >
                          {minutes}
                          <span className="text-base ml-0.5">M</span>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="ml-12 text-2xl font-semibold text-trip-kpi-value font-poppins">
                      {kpi.value}

                      {kpi.unit && (
                        <span className="text-base ml-0.5">{kpi.unit}</span>
                      )}
                    </p>
                  )}
                </div>
              </Col>
            );
          })}
      </Row>
      <Row gutter={8} className="mt-2">
        {CompletedTripData(tripDetailsKPI, overallSummaryForTrip)
          ?.slice(6)
          ?.map((kpi) => {
            return (
              <Col span={8} key={kpi.id}>
                <div className="p-4 rounded-md bg-kpi-background">
                  <div className="flex items-center gap-x-2.5">
                    <CustomRoundedImage
                      background="bg-kpi-icon"
                      image={kpi.icon}
                    />
                    <p className="text-xs font-normal capitalize font-aileron text-trip-kpi-title">
                      {kpi.title}
                    </p>
                  </div>

                  <p className="ml-12 text-2xl font-semibold text-trip-kpi-value font-poppins">
                    {kpi.value}

                    {kpi.unit && (
                      <span className="text-base ml-0.5">{kpi.unit}</span>
                    )}
                  </p>
                </div>
              </Col>
            );
          })}
      </Row>

      <Row gutter={8} className="mt-2">
        {CompletedTripData(tripDetailsKPI)
          ?.slice(3, 6)
          ?.map((kpi) => (
            <Col span={8} key={kpi.id}>
              <div className="p-4 rounded-md bg-kpi-background">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-x-2.5">
                    <CustomRoundedImage
                      background="bg-kpi-icon"
                      image={kpi.icon}
                    />
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs font-normal capitalize font-aileron text-trip-kpi-title">
                        {kpi.title}
                      </p>
                      {kpi.id === 5 && isVarianceDetected && (
                        <KpiAlertTooltip text={varianceTooltip} />
                      )}
                    </div>
                  </div>

                  {kpi.id === 6 && <SpoiltMilkTooltip />}
                </div>

                <div className="ml-12 flex items-center justify-between mr-[22px]">
                  <p
                    className={`text-2xl font-semibold font-poppins ${
                      kpi.id === 5 && isVarianceDetected
                        ? "text-[#C84630]"
                        : "text-trip-kpi-value"
                    }`}
                  >
                    {kpi.value}
                    {kpi.unit && (
                      <span className="text-base ml-0.5">{kpi.unit}</span>
                    )}
                  </p>

                  {kpi.id === 6 && (
                    <div
                      role="button"
                      onClick={() =>
                        dispatch(
                          updateTrips({
                            key: "spoiltMilkModal",
                            value: {
                              visible: true,
                              stops: tripDetailsKPI?.spoiltMilkForStops ?? 0,
                              excess: tripDetailsKPI?.excessSpoiltMilk ?? 0,
                            },
                          }),
                        )
                      }
                    >
                      <UpdateIcon />
                    </div>
                  )}
                </div>
              </div>
            </Col>
          ))}
      </Row>
      <div className="mt-6">
        <TripRequests />
      </div>
      {/* {hasTenantTrikePermission && (
        <Row className="mt-6">
          <Col span={24}>
            <TripInformation tripChecklist={tripChecklist} />
          </Col>
        </Row>
      )} */}

      {/* <Row className="mt-6">
        <Col span={24}>
          <Commission />
        </Col>
      </Row> */}
      <Row className="mt-6">
        <Col span={24}>
          <StopDetails />
        </Col>
      </Row>
    </div>
  );
};

export default TripSummaryStops;
